import React, { useRef } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useFieldOwners, isCaretCursorEligibleFieldKey } from '../../hooks/usePresence'
import { DEFAULT_AVATAR_COLOUR } from './Avatar'
import PresenceAvatar from './PresenceAvatar'
import FieldCursorOverlay from './FieldCursorOverlay'
interface FormFieldProps {
  // ReactNode (was string) so a label can carry an inline tag (the case
  // sections' dashed "shown for Other" condition tag). Strings still work.
  label: React.ReactNode
  required?: boolean
  helper?: string
  children: React.ReactNode
  // Stage 2d — outdated indicator. When true, a small amber dot
  // (bg-amber-400, w-2 h-2 rounded-full) renders at the top-right of
  // the label. outdatedLiveValue, when provided, is shown in the dot's
  // native title tooltip as "Current client has: <value>". Default
  // undefined → no dot rendered → zero visual impact on every other
  // consumer of FormField across the app (Defendant, TPI, Settings,
  // etc.). The label's layout switched from `block` to `flex
  // items-center justify-between` to give the dot a stable right-edge
  // home; when no dot renders, the flex container has one child and
  // looks identical to the previous block layout.
  outdated?: boolean
  outdatedLiveValue?: string
  // Phase 2a — field-level presence. When set, this field participates:
  //   (a) the wrapper is tagged data-field=<fieldKey> so a section's
  //       delegated focusin/focusout listener can resolve which field
  //       gained/lost focus (via closest('[data-field]')), and
  //   (b) when ANOTHER user is focused on this field, a small avatar +
  //       a coloured ring render, marking it as "someone's editing here".
  // Omitted (the default, and every consumer outside the ClientDetails
  // pilot) → NO presence behaviour, NO subscription, byte-identical to
  // before. Presence is display-only: it never touches the field's value
  // or the save path.
  fieldKey?: string
  // 'info' — the client-info sections look (ClientInfoSections, the Client
  // Details redesign): sentence-case 12px semibold label, no bottom margin
  // (the section grid owns spacing). Omitted → the original look, unchanged.
  variant?: 'info'
  // Optional id the label points at (htmlFor) — 'info' fields pass it.
  htmlFor?: string
}
const LABEL_DEFAULT = 'flex items-center justify-between text-xs font-normal uppercase tracking-widest text-casevo-brand mb-1'
const LABEL_INFO = 'flex items-center justify-between text-[12px] font-semibold text-casevo-brand dark:text-casevo-mid mb-[5px]'

// The amber "outdated" dot — shared by both variants.
const OutdatedDot: React.FC<{ outdatedLiveValue?: string }> = ({ outdatedLiveValue }) => (
  <span
    aria-label="Out of date"
    title={outdatedLiveValue ? `Current client has: ${outdatedLiveValue}` : 'Out of date'}
    className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0"
  />
)

// PLAIN variant — byte-identical to the pre-Phase-2a FormField. Used by
// every consumer that doesn't pass fieldKey (i.e. everything except the
// ClientDetails pilot), so nothing else in the app changes.
const PlainFormField: React.FC<Omit<FormFieldProps, 'fieldKey'>> = ({
  label,
  required = false,
  helper,
  children,
  outdated = false,
  outdatedLiveValue,
  variant,
  htmlFor,
}) => (
  <div className={variant === 'info' ? 'min-w-0' : 'mb-4'}>
    <label htmlFor={htmlFor} className={variant === 'info' ? LABEL_INFO : LABEL_DEFAULT}>
      <span>
        {label}
        {required && <span className="text-casevo-danger ml-1">*</span>}
      </span>
      {outdated && <OutdatedDot outdatedLiveValue={outdatedLiveValue} />}
    </label>
    {children}
    {helper && <p className="text-xs text-gray-400 dark:text-casevo-text-soft mt-1">{helper}</p>}
  </div>
)

// PRESENCE variant — same layout as PlainFormField PLUS: data-field on the
// wrapper (delegation target), an avatar cluster of OTHER users editing
// this field (self excluded), and a coloured ring around the input while
// someone else is in it. Subscribes to the field-owner channel; only the
// ~pilot fields that pass fieldKey mount this, so subscriptions stay
// scoped. Additive + graceful: socket down → owners empty → renders
// exactly like the plain variant (minus the ring/avatar), field works.
const PresenceFormField: React.FC<FormFieldProps & { fieldKey: string }> = ({
  label,
  required = false,
  helper,
  children,
  outdated = false,
  outdatedLiveValue,
  fieldKey,
  variant,
  htmlFor,
}) => {
  const owners = useFieldOwners()
  // Positioning context for the Stage-A live-cursor overlay (pilot field only).
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selfId = useCasevoStore((s) => s.authUser?.id)
  // OTHER users focused on THIS field (exclude yourself — you don't need
  // to be told you're editing your own field).
  const others = owners.filter((o) => o.fieldKey === fieldKey && o.userId !== selfId)
  const active = others.length > 0
  // Colour of the left accent bar = the (first) other editor's avatar colour.
  const accentColour = active ? (others[0].avatarColour || DEFAULT_AVATAR_COLOUR) : undefined

  return (
    // `relative` anchors the accent bar; harmless when inactive (no offset,
    // no layout shift). data-field drives the document focus listener.
    <div ref={wrapperRef} className={variant === 'info' ? 'min-w-0 relative' : 'mb-4 relative'} data-field={fieldKey}>
      {/* ★ LEFT ACCENT BAR (Phase 2b fix) — replaces the old boxShadow ring
          that wrapped {children} and so enclosed the red FieldError on
          required fields + boxed composite fields (radio groups, the
          Accident-location toggle+textarea) awkwardly. A thin coloured
          stripe down the field's left edge marks the WHOLE field cleanly
          for EVERY structure, never "extends under" anything. It sits in
          the section's left padding gutter (-left-2) so it adds no content
          padding and causes no horizontal shift when it appears. The label
          avatar remains the "who"; this bar is the "which field". */}
      {active && (
        <span
          aria-hidden="true"
          className="absolute -left-2 top-0 bottom-0 w-[3px] rounded-full"
          style={{ backgroundColor: accentColour }}
        />
      )}
      <label htmlFor={htmlFor} className={variant === 'info' ? LABEL_INFO : LABEL_DEFAULT}>
        <span>
          {label}
          {required && <span className="text-casevo-danger ml-1">*</span>}
        </span>
        <span className="flex items-center gap-1.5 shrink-0">
          {outdated && <OutdatedDot outdatedLiveValue={outdatedLiveValue} />}
          {active && (
            // The field-owner presence chip(s) — the SAME ~20px photo/initials
            // owner-colour avatar the Yjs cursor shows (PresenceAvatar), so a
            // person looks identical editing a note vs a form field. Multiple
            // editors overlap (-space-x-1.5) with a surface ring separating them,
            // mirroring the "also viewing" cluster.
            <span className="inline-flex items-center -space-x-1.5">
              {others.map((o) => (
                <span key={o.userId} className="rounded-full ring-2 ring-casevo-surface">
                  <PresenceAvatar
                    userId={o.userId}
                    name={o.name}
                    colour={o.avatarColour}
                    photoUrl={o.avatarPhotoUrl}
                    title={`${o.name} is editing this field`}
                  />
                </span>
              ))}
            </span>
          )}
        </span>
      </label>
      {/* Children render directly (no wrapping ring div) — the left accent
          bar above marks the field, so nothing boxes the input/error. */}
      {children}
      {helper && <p className="text-xs text-gray-400 dark:text-casevo-text-soft mt-1">{helper}</p>}
      {/* Stage B — live remote text cursor for ALL eligible plain text inputs.
          PERF: mount the overlay ONLY when a remote user is focused on THIS field
          (`active`) and it's a cursor-eligible fieldKey (not an address-search) —
          so the overlay machinery + its caret subscription exist only during
          active co-editing, not on every field. The overlay itself then also
          checks the input TYPE (excludes select/date/checkbox/radio/Yjs) and a
          live caret before rendering anything. */}
      {active && isCaretCursorEligibleFieldKey(fieldKey) && (
        <FieldCursorOverlay wrapperRef={wrapperRef} fieldKey={fieldKey} active={active} />
      )}
    </div>
  )
}

const FormField: React.FC<FormFieldProps> = (props) => {
  // Route to the presence variant ONLY when a fieldKey is supplied. This
  // keeps the hook/subscription off every other FormField in the app.
  if (props.fieldKey) return <PresenceFormField {...props} fieldKey={props.fieldKey} />
  return <PlainFormField {...props} />
}
export default FormField
